import {
  GetAllMetaobjects,
  GetProductByHandle,
  GetProductById,
  GetProductOptionsById,
  GetProductVariantsByBatch,
  GetProductsByBatch,
  GetVariantById,
} from '@dna/queries'
import { isStringifiedArray, splitProductName } from '@dna/utils'
import '@vendor/storefront-client'

const registerGlobal = window.DNARegisterGlobal

/**
 * @typedef {Object} CleanUpOptions
 * @property {Boolean} [allowPDPImages] - if PDP images should be included in returned data
 */

/**
 * A class leveraging Shopify's 1st party storefront-api-client for requesting data from Storefront API
 */
class StorefrontClient {
  /**
   * @param {string} domain - The domain of the Shopify store.
   * @param {string} accessToken - The access token for the Storefront API.
   * @param {string} apiVersion - The API version to use.
   */
  constructor(
    storeDomain = __DNA_GLOBAL__.domain,
    apiVersion = '2024-10',
    publicAccessToken = __DNA_GLOBAL__.storefrontToken,
  ) {
    this.client = ShopifyStorefrontAPIClient.createStorefrontApiClient({
      storeDomain,
      apiVersion,
      publicAccessToken,
    })
  }

  /**
   * Fetch product data by its ID.
   * @param {string} id - The product ID.
   * @returns {Promise<ClientResponse<TData>>} A promise to return GraphQL data
   */
  async getProductById(id) {
    const response = await this.client.request(GetProductById, {
      variables: { id: `gid://shopify/Product/${id}` },
    })
    return cleanUpProductData(response?.data?.node)
  }

  /**
   * Fetch product option data by its ID.
   * @param {string} id - The product ID.
   * @returns {Promise<ClientResponse<TData>>} A promise to return GraphQL data
   */
  async getProductOptionsById(id) {
    const response = await this.client.request(GetProductOptionsById, {
      variables: { id: `gid://shopify/Product/${id}` },
    })
    return cleanUpProductData(response?.data?.node)
  }

  /**
   * Fetch product data by its handle.
   * @param {string} id - The product handle.
   * @param {CleanUpOptions} cleanUpOptions - Options for toggling various behaviors
   * @returns {Promise<ClientResponse<TData>>} A promise to return GraphQL data
   */
  async getProductByHandle(handle, cleanUpOptions = {}) {
    const response = await this.client.request(GetProductByHandle, {
      variables: { handle },
    })
    return cleanUpProductData(response?.data?.product, cleanUpOptions)
  }

  /**
   * Fetch variant data by its ID.
   * @param {string} id - The variant ID.
   * @returns {Promise<ClientResponse<TData>>} A promise to return GraphQL data
   */
  async getVariantById(id) {
    const response = await this.client.request(GetVariantById, {
      variables: { id: `gid://shopify/ProductVariant/${id}` },
    })
    return cleanUpVariantData(response?.data?.node)
  }

  /**
   * Fetch batch products data by IDs.
   * @param {Array} ids - Product IDs.
   * @returns {Promise<ClientResponse<TData>>} A promise to return GraphQL data
   */
  async getProductsByBatch(ids) {
    const structuredIds = []
    for (let i = 0; i < ids.length; i++) {
      structuredIds.push(`gid://shopify/Product/${ids[i]}`)
    }

    const response = await this.client.request(GetProductsByBatch, {
      variables: { ids: structuredIds },
    })

    return cleanUpProductsData(response?.data)
  }

  /**
   * Fetch batch products data by IDs.
   * @param {Array} ids - Product IDs.
   * @returns {Promise<ClientResponse<TData>>} A promise to return GraphQL data
   */

  async getProductVariantsByBatch(ids) {
    const structuredIds = []
    for (let i = 0; i < ids.length; i++) {
      structuredIds.push(`gid://shopify/Product/${ids[i]}`)
    }
    const response = await this.client.request(GetProductVariantsByBatch, {
      variables: { ids: structuredIds },
    })
    return cleanUpProductsData(response?.data)
  }

  /**
   * Fetch all metaobject entries by definition type
   * @param {string} handle - The metaobject definition type.
   * @returns {Promise<ClientResponse<TData>>} A promise to return GraphQL data
   */
  async GetAllMetaobjectsByType(type) {
    const response = await this.client.request(GetAllMetaobjects, {
      variables: { type },
    })
    return cleanUpMetaobjects(response?.data?.metaobjects?.edges)
  }
}

/**
 * GraphQL returns numbers in dollars and as a string. This function safely converts it
 * to a number object in cents.
 * @param {string|number} price - The price value.
 * @returns {number}
 */
const normalizePrice = (price) => {
  if ([null, undefined].includes(price)) return null
  return Number(price) * 100
}

/**
 * Strips GraphQL Global ID (GID) down to its numeric component.
 * @param {string} value - The GID.
 * @returns {string} The stripped GID.
 */
function stripGID(value) {
  const gidPattern = /^gid:\/\/shopify\/\w+\//
  if (gidPattern.test(value)) {
    return value.replace(gidPattern, '')
  }
  return value
}

/**
 * Makes metafield data from GraphQL workable with JS.
 * @param {Array<Object>} rawMetafields - The GraphQL response for metafields.
 * @param {CleanUpOptions} cleanUpOptions - Options for toggling various behaviors
 * @returns {Object} The processed metafield data.
 */
function cleanUpMetafields(rawMetafields, cleanUpOptions = {}) {
  if (!rawMetafields) return {}
  return rawMetafields
    .filter((m) => m !== null)
    .reduce((acc, metafield) => {
      if (!acc[metafield.namespace]) {
        acc[metafield.namespace] = {}
      }
      let value
      if (Object.keys(metafield.reference || {}).includes('image')) {
        value = metafield.reference.image.originalSrc
      } else if (Object.keys(metafield.reference || {}).includes('url')) {
        value = metafield.reference.url
      } else if (isStringifiedArray(metafield.value)) {
        const array = JSON.parse(metafield.value)
        value = array.map((i) => stripGID(i))
      } else if (Object.keys(metafield.reference || {}).includes('handle')) {
        value = metafield.reference.handle
      } else {
        value = stripGID(metafield.value)
      }
      acc[metafield.namespace][metafield.key] = value
      return acc
    }, {})
}

/**
 * Cleans up the metaobjects response data and extracts relevant information.
 *
 * @param {Array} metaobjects - The array of metaobject edges from the GraphQL response.
 * @param {CleanUpOptions} cleanupOptions - Options for toggling various behaviors
 * @returns {Array} An array of cleaned-up metaobject objects.
 */
function cleanUpMetaobjects(metaobjects, cleanUpOptions = {}) {
  if (!Array.isArray(metaobjects)) {
    console.warn('Invalid metaobjects data. Expected an array.')
    return []
  }

  return metaobjects
    .map((edge) => {
      if (!edge || !edge.node) {
        console.warn('Invalid metaobject edge. Skipping.')
        return null
      }

      const { node } = edge
      const { id, handle, type, updatedAt, onlineStoreUrl, fields } = node

      const cleanedFields = Array.isArray(fields)
        ? fields.reduce((acc, field) => {
            if (!field || !field.key) {
              console.warn('Invalid field data. Skipping.')
              return acc
            }
            acc[field.key] = field.value
            return acc
          }, {})
        : {}

      return {
        id: stripGID(id),
        handle,
        type,
        updatedAt,
        onlineStoreUrl,
        fields: cleanedFields,
      }
    })
    .filter(Boolean)
}

/**
 * Makes product data from GraphQL workable with JS.
 * @param {Object} rawProduct - The GraphQL response for product.
 * @param {CleanUpOptions} cleanupOptions - Options for toggling various behaviors
 * @returns {Object|null} The processed product data.
 */
function cleanUpProductData(rawProduct, cleanUpOptions = {}) {
  if (!rawProduct) {
    return null
  }

  const variantsSortedByPrice =
    rawProduct.variants?.edges?.sort((a, b) => {
      const priceA = Number.parseFloat(a.node.priceV2.amount)
      const priceB = Number.parseFloat(a.node.priceV2.amount)
      if (priceA < priceB) return -1
      if (priceA > priceB) return 1
      return 0
    }) || []
  const lowestPriceVariant = variantsSortedByPrice[0]
  let compareAtPrice = null
  if (variantsSortedByPrice.length) {
    const compareAtPriceProperty =
      lowestPriceVariant.node.compareAtPriceV2?.amount
    if (compareAtPriceProperty)
      compareAtPrice = Number.parseFloat(compareAtPriceProperty)
  }

  let colorOptions = []
  let sizeOptions = []

  let giftCardOptions = []
  let productOptions = rawProduct.options?.map((opt) => {
    if (opt.name === 'Color' && opt.optionValues) {
      colorOptions = cleanUpProductOptionData(opt.optionValues, cleanUpOptions)
    }

    if (opt.name === 'Size' && opt.optionValues) {
      sizeOptions = cleanUpProductOptionData(opt.optionValues, cleanUpOptions)
    }

    if (opt.name === 'Gift Card Designs' && opt.optionValues) {
      giftCardOptions = cleanUpProductOptionData(
        opt.optionValues,
        cleanUpOptions,
      )
    }
    return opt
  })
  productOptions = {
    colorOptions,
    sizeOptions,
    giftCardOptions,
  }
  return {
    id: stripGID(rawProduct.id),
    available: rawProduct.availableForSale,
    title: rawProduct.title,
    description: rawProduct.descriptionHtml,
    minPrice: normalizePrice(rawProduct.priceRange?.minVariantPrice?.amount),
    maxPrice: normalizePrice(rawProduct.priceRange?.maxVariantPrice?.amount),
    compareAtPrice: normalizePrice(compareAtPrice),
    handle: rawProduct.handle,
    type: rawProduct.productType,
    vendor: rawProduct.vendor,
    createdAt: new Date(rawProduct.createdAt),
    updatedAt: new Date(rawProduct.updatedAt),
    publishedAt: new Date(rawProduct.publishedAt),
    onlineStoreUrl: rawProduct.onlineStoreUrl,
    tags: rawProduct.tags,
    media: rawProduct?.media?.edges
      .map((edge) => cleanUpMediaData(edge.node, cleanUpOptions))
      .filter((media) => media !== null),
    options: productOptions,
    collections: rawProduct.collections?.edges.map((edge) => {
      return cleanUpCollectionData(edge, cleanUpOptions)
    }),
    variants: rawProduct.variants?.edges.map((edge) => {
      return cleanUpVariantData(edge.node, cleanUpOptions)
    }),
    metafields: cleanUpMetafields(rawProduct.metafields, cleanUpOptions),
    isDiscounted: rawProduct.variants?.edges.some((variantEdge) => {
      const variant = variantEdge.node
      const originalPrice = Number.parseFloat(variant.priceV2.amount)
      const compareAtPrice = Number.parseFloat(variant.compareAtPriceV2?.amount)
      return compareAtPrice > originalPrice
    }),
    pageTitle: rawProduct.seo?.title,
  }
}

/**
 * Makes batch products data from GraphQL workable with JS.
 * @param {Array} rawProducts - The GraphQL response for batch products.
 * @param {CleanUpOptions} cleanupOptions - Options for toggling various behaviors
 * @returns {Array|null} The processed product data.
 */
function cleanUpProductsData(rawData, cleanUpOptions = {}) {
  if (!rawData) {
    return null
  }
  return rawData.nodes.map((product) =>
    cleanUpProductData(product, cleanUpOptions),
  )
}

/**
 * Makes media data from GraphQL workable with JS.
 * @param {Object} rawVariant - The GraphQL response for media.
 * @param {CleanUpOptions} cleanupOptions - Options for toggling various behaviors
 * @returns {Object|null} The processed media data.
 */
function cleanUpMediaData(rawMedia, cleanUpOptions = {}) {
  if (
    !rawMedia ||
    (rawMedia.alt?.startsWith('PDP |') && !('allowPDPImages' in cleanUpOptions))
  ) {
    return null
  }
  const type = rawMedia?.mediaContentType.toLowerCase()
  switch (type) {
    case 'image':
      return {
        type,
        url: rawMedia?.image?.url,
        alt: rawMedia?.alt,
      }
    case 'video':
      return {
        type,
        url: rawMedia?.sources[0]?.url,
        alt: rawMedia?.alt,
        poster: rawMedia?.previewImage?.url,
      }
  }
}

/**
 * Makes variant data from GraphQL workable with JS.
 * @param {Object} rawVariant - The GraphQL response for variant.
 * @param {CleanUpOptions} cleanupOptions - Options for toggling various behaviors
 * @returns {Object|null} The processed variant data.
 */
function cleanUpVariantData(rawVariant, cleanUpOptions = {}) {
  if (!rawVariant) {
    return null
  }
  return {
    id: stripGID(rawVariant?.id),
    title: rawVariant?.title,
    price: normalizePrice(rawVariant?.priceV2?.amount),
    compareAtPrice: normalizePrice(rawVariant?.compareAtPriceV2?.amount),
    currencyCode: rawVariant?.priceV2?.currencyCode,
    available: rawVariant?.availableForSale,
    sku: rawVariant?.sku,
    selectedOptions: rawVariant?.selectedOptions,
    quantityAvailable: rawVariant?.quantityAvailable,
    metafields: cleanUpMetafields(rawVariant?.metafields, cleanUpOptions),
    image: {
      url: rawVariant?.image?.url,
      alt: rawVariant?.image?.altText,
    },
  }
}

/**
 * Process the search data response.
 * @param {Object} rawData - The raw search data.
 * @param {CleanUpOptions} cleanupOptions - Options for toggling various behaviors
 * @returns {Array<Object>} The list of processed product data.
 */
function cleanUpSearchData(rawData, cleanUpOptions = {}) {
  if (!rawData) {
    return null
  }
  return rawData.products.edges.map((product) =>
    cleanUpProductData(product, cleanUpOptions),
  )
}

/**
 * Makes collection data from GraphQL workable with JS.
 * @param {Object} rawData - The GraphQL response for collection.
 * @param {CleanUpOptions} cleanupOptions - Options for toggling various behaviors
 * @returns {Object|null} The processed collection data.
 */
function cleanUpCollectionData(rawData, cleanUpOptions) {
  if (!rawData || !rawData.node) {
    return null
  }

  const rawCollection = rawData.node

  return {
    id: stripGID(rawCollection.id),
    title: rawCollection.title,
    handle: rawCollection.handle,
    description: rawCollection.descriptionHtml,
    updatedAt: new Date(rawCollection.updatedAt),
    publishedAt: new Date(rawCollection.publishedAt),
    image: rawCollection.image,
    products: rawCollection.products?.edges.map((edge) => {
      return cleanUpProductData(edge, cleanUpOptions)
    }),
    metafields: cleanUpMetafields(rawCollection.metafields, cleanUpOptions),
  }
}

/**
 * Makes product option data from GraphQL workable with JS
 * @param {Array} options - The GraphQL response for product options
 * @param {CleanUpOptions} cleanupOptions - Options for toggling various behaviors
 * @returns {Array|null} The processed product option data.
 */
function cleanUpProductOptionData(options, cleanUpOptions = {}) {
  const allOptions = options.map((option) => {
    return {
      available: option.firstSelectableVariant.availableForSale,
      color: splitProductName(option.firstSelectableVariant.product.title)
        .color,
      compareAtPrice: normalizePrice(
        option.firstSelectableVariant.compareAtPrice?.amount,
      ),
      handle: option.firstSelectableVariant.product.handle,
      id: option.firstSelectableVariant.product.id.split('/').pop(),
      media: option.firstSelectableVariant.product.media.edges
        .map((edge) => cleanUpMediaData(edge.node, cleanUpOptions))
        .filter((media) => media !== null),
      metafields: cleanUpMetafields(
        option.firstSelectableVariant.product.metafields,
        cleanUpOptions,
      ),
      price: normalizePrice(option.firstSelectableVariant.price?.amount),
      tags: option.firstSelectableVariant.product.tags,
      title: splitProductName(option.firstSelectableVariant.product.title)
        .style,
      url: option.firstSelectableVariant.product.onlineStoreUrl,
      variant_id: option.firstSelectableVariant.id.split('/').pop(),
      sku: option.firstSelectableVariant.sku,
      onlineStoreUrl: option.firstSelectableVariant.product.onlineStoreUrl,
      variant_title: option.firstSelectableVariant.title,
    }
  })
  return allOptions
}

/**
 * Maps an array of metaobjects to an object where the keys are the "handle" values.
 *
 * @param {Array} metaobjects - An array of metaobject objects.
 * @returns {Object} An object where the keys are the "handle" values and the values are the corresponding metaobject properties (excluding "handle").
 */
export function mapMetaobjectsByHandle(metaobjects) {
  if (!Array.isArray(metaobjects)) {
    console.warn('Invalid metaobjects data. Expected an array.')
    return {}
  }

  return metaobjects.reduce((acc, metaobject) => {
    if (!metaobject || !metaobject.handle) {
      console.warn('Invalid metaobject data. Skipping.')
      return acc
    }

    const { handle, ...rest } = metaobject
    acc[handle] = rest
    return acc
  }, {})
}

/**
 * This is a simple wrapper class that adds performance markers to
 * all storefront api calls
 */
class ProfilingClient extends StorefrontClient {
  constructor() {
    super()
    this.performanceEnabled =
      window?.performance?.mark && window?.performance?.measure
  }

  _startRequest(name) {
    if (this.performanceEnabled) {
      window.performance.mark(`rothys.storefront.${name}.start`)
    }
  }

  _endRequest(name) {
    if (this.performanceEnabled) {
      window.performance.mark(`rothys.storefront.${name}.end`)
      try {
        window.performance.measure(`rothys.storefront.${name}`, {
          start: `rothys.storefront.${name}.start`,
          end: `rothys.storefront.${name}.end`,
          detail: {
            devtools: {
              track: 'Storefront API',
              trackGroup: 'Rothys',
            },
          },
        })
      } catch (e) {
        console.error(e)
      }
    }
  }

  async getProductById(id) {
    this._startRequest(`getProductById:${id}`)
    const response = await super.getProductById(id)
    this._endRequest(`getProductById:${id}`)
    return response
  }

  async getProductOptionsById(id) {
    this._startRequest(`getProductOptionsById:${id}`)
    const response = await super.getProductOptionsById(id)
    this._endRequest(`getProductOptionsById:${id}`)
    return response
  }

  async getProductByHandle(handle, cleanUpOptions = {}) {
    this._startRequest(`getProductByHandle:${handle}`)
    const response = await super.getProductByHandle(handle, cleanUpOptions)
    this._endRequest(`getProductByHandle:${handle}`)
    return response
  }

  async getVariantById(id) {
    this._startRequest(`getVariantById:${id}`)
    const response = await super.getVariantById(id)
    this._endRequest(`getVariantById:${id}`)
    return response
  }

  async getProductsByBatch(ids) {
    this._startRequest(`getProductsByBatch:${ids}`)
    const response = await super.getProductsByBatch(ids)
    this._endRequest(`getProductsByBatch:${ids}`)
    return response
  }

  async getProductVariantsByBatch(ids) {
    this._startRequest(`getProductVariantsByBatch:${ids}`)
    const response = await super.getProductVariantsByBatch(ids)
    this._endRequest(`getProductVariantsByBatch:${ids}`)
    return response
  }

  async GetAllMetaobjectsByType(type) {
    this._startRequest(`GetAllMetaobjectsByType:${type}`)
    const response = await super.GetAllMetaobjectsByType(type)
    this._endRequest(`GetAllMetaobjectsByType:${type}`)
    return response
  }
}

// Export singleton for client class to prevent need to instantiate each time
export const storefrontClient = new ProfilingClient()

// Expose critical dependencies to the global namespace for use outside of JS modules
registerGlobal(mapMetaobjectsByHandle, 'mapMetaobjectsByHandle', 'utils')
registerGlobal(storefrontClient, 'StorefrontClient')
